/**
 * Visibility rule engine for addons and one-time products.
 *
 * Extracted verbatim from the useCallback pair that lived in DynamicForm so the same rules
 * apply on the server. Previously visibility was consulted only when collecting lookup
 * keys, never when computing displayed totals -- so the price on screen and the price sent
 * to Stripe could disagree. resolveCart() now applies this in one place.
 *
 * Plain ESM, no React, no Node built-ins: must run in the Workers runtime.
 */

/**
 * Does a single rule match the current selection?
 * An absent field means "no constraint", so {} matches everything.
 */
export function matchesRule(rule, ctx) {
  const termOk = !rule?.terms || rule.terms.includes(ctx.term);
  const primaryOk = !rule?.primary || rule.primary.includes(ctx.primaryPlanId);
  return termOk && primaryOk;
}

/**
 * Is this catalog item visible for the given term + primary plan?
 *
 * ⚠️  PRECEDENCE, preserved from the original implementation and non-obvious:
 * a non-empty `visibilityRules` array wins OUTRIGHT. When it is present,
 * availableTerms / includeForPrimary / excludeForPrimary are ignored entirely -- they are
 * not additional constraints. Several product-config entries carry both (with the others
 * commented out as alternatives), so changing this would silently alter which products
 * customers can buy. Pinned by a test.
 *
 * @param {object} item catalog entry (addon, one-time product, or primary plan)
 * @param {{term: string, primaryPlanId: ?string}} ctx
 */
export function isItemVisible(item, ctx) {
  if (Array.isArray(item?.visibilityRules) && item.visibilityRules.length > 0) {
    return item.visibilityRules.some((rule) => matchesRule(rule, ctx));
  }

  const termOk = !Array.isArray(item?.availableTerms) || item.availableTerms.includes(ctx.term);
  const includeOk =
    !Array.isArray(item?.includeForPrimary) || item.includeForPrimary.includes(ctx.primaryPlanId);
  const excludeOk =
    !Array.isArray(item?.excludeForPrimary) || !item.excludeForPrimary.includes(ctx.primaryPlanId);

  return termOk && includeOk && excludeOk;
}

/** Filter a list of catalog items by visibility. */
export function filterVisible(items, ctx) {
  return (items || []).filter((item) => isItemVisible(item, ctx));
}
